import { useState, useCallback, useMemo } from "react";
import BreakdownDialog from "./BreakdownDialog";

const BUCKET_TONE = {
  Compliant: "green",
  Pending: "blue",
  Overdue: "amber",
  Critical: "red",
};

function bucketTone(bucket) {
  return BUCKET_TONE[bucket] || "navy";
}

function daysOverdueTone(days) {
  if (!Number.isFinite(days) || days <= 0) return "green";
  if (days <= 30) return "amber";
  return "red";
}

export const OBLIGATION_STATUS_BREAKDOWN_COLUMNS = [
  { key: "obligation_type", label: "Obligation Type" },
  { key: "asset", label: "Asset" },
  { key: "lessor_name", label: "Lessor" },
  { key: "status_label", label: "Status" },
  { key: "due_date", label: "Due Date", type: "date" },
  { key: "days_overdue", label: "Days Overdue", type: "pill" },
  // { key: "bucket", label: "Bucket", type: "pill" },
];

function buildObligationStatusBreakdownRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((item, index) => {
    const days = typeof item?.days_overdue === "number" ? item.days_overdue : parseFloat(item?.days_overdue);
    const hasDays = Number.isFinite(days);
    return {
      id: item?.slug || item?.obligation_id || index,
      obligation_type: item?.obligation_type?.label || "-",
      asset: item?.asset?.name ?? "-",
      lessor_name: item?.lessor_name || "Unassigned",
      status_label: item?.status_label || "-",
      due_date: item?.payment_due_date,
      days_overdue: hasDays ? { label: `${days} days`, tone: daysOverdueTone(days) } : { label: "-", tone: "navy" },
      bucket: { label: item?.bucket || "-", tone: bucketTone(item?.bucket) },
      _bucket: item?.bucket || "Unknown",
    };
  });
}

// Drives the "Obligation Status Overview" popup: clicking a pie slice/legend
// entry for a status bucket filters obligation_breakdown down to that bucket's obligations.
export function useObligationStatusBreakdown({ obligationBreakdown, onTrack }) {
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const reset = useCallback(() => {
    setSelectedBucket(null);
    setModalOpen(false);
  }, []);

  const allRows = useMemo(() => buildObligationStatusBreakdownRows(obligationBreakdown), [obligationBreakdown]);
  const canRender = allRows.length > 0;

  const rows = useMemo(() => {
    if (!selectedBucket) return allRows;
    return allRows.filter((row) => row._bucket === selectedBucket);
  }, [allRows, selectedBucket]);

  const handleChartClick = useCallback(
    (params) => {
      if (!params || params.componentType !== "series") return;
      const bucket = params.name || null;
      setSelectedBucket(bucket);
      setModalOpen(true);
      onTrack?.(`Graph Slice Clicked: ${bucket || ""}`);
    },
    [onTrack],
  );

  const chartEvents = useMemo(() => ({ click: handleChartClick }), [handleChartClick]);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const titleSuffix = useMemo(() => selectedBucket || "", [selectedBucket]);

  return { rows, canRender, chartEvents, modalOpen, closeModal, titleSuffix, reset };
}

export function ObligationStatusBreakdownModal({ open, onClose, rows, titleSuffix }) {
  const title = "Obligation Status Overview";
  return (
    <BreakdownDialog
      open={open}
      onClose={onClose}
      title={title}
      subtitle={titleSuffix}
      columns={OBLIGATION_STATUS_BREAKDOWN_COLUMNS}
      rows={rows}
    />
  );
}
